import { useState, type FormEvent } from 'react'
import { useClinicalBridge } from '../../schedule/index.js'
import { useUpdateWorker, useWorkerProfile } from '../index.js'
import type { WorkerProfile } from '../index.js'
import { Modal } from '../../core/index.js'
import { FORM_INPUT_CLASS, FormError } from '../../core/index.js'
import { useWorkerContacts } from '../../messages/index.js'
import { WorkerBridgeField, WorkerMobileField, WorkerNameField, WorkerReadingsFields, WorkerRoleField, WorkerEmploymentField, WorkerStaffingFields, hasWorkerName, workerDetailChanges, workerDetailValues, type WorkerDetailValues } from './WorkerFormFields.js'
import { WorkerLoginSection } from '../../accounts/index.js'
import { operatorWorkerLoginApi, workerLoginQueryKey } from '../../accounts/index.js'
import { Button } from '../../core/index.js'
import { Save } from 'lucide-react'

const FORM_ID = 'edit-worker-form'
const ID_PREFIX = 'edit-worker'

interface EditWorkerModalProps {
  clientId: string | undefined
  facility: { facilityId: string; name: string }
  workerId: string
  onClose: () => void
}

/**
 * An operator, or a facility administrator at one of their own facilities,
 * changing what a person may change about a worker on the roster: the name,
 * role, hire date, readings, Clinical Bridge eligibility and, below the form,
 * the worker's login. The identity is shown but fixed, and
 * the tier and score are not here - the tier moves at a period close (or by
 * override) and the score is the engine's. Loads the stored profile first so
 * the form starts from what is saved, and sends only the fields that differ.
 */
export function EditWorkerModal({ clientId, facility, workerId, onClose }: EditWorkerModalProps) {
  const profileQuery = useWorkerProfile(workerId)
  // The Clinical Bridge eligibility is on the worker's Bridge record, read beside the profile so the form starts from both.
  const bridgeQuery = useClinicalBridge(workerId)
  const subtitle = (
    <span>
      {facility.name}
    </span>
  )

  const isPending = profileQuery.isPending || bridgeQuery.isPending
  if (isPending || profileQuery.isError || bridgeQuery.isError) {
    return (
      <Modal eyebrow="Edit worker" title={isPending ? 'Loading…' : 'Worker could not be loaded'} subtitle={subtitle} onClose={onClose} footer={<CancelButton onClose={onClose} />}>
        <div className="bg-canvas p-6 text-sm text-ink-mid">{isPending ? 'Fetching the worker’s record.' : 'Close this and try again.'}</div>
      </Modal>
    )
  }

  return <EditWorkerForm clientId={clientId} facility={facility} profile={profileQuery.data} bridgeEligible={bridgeQuery.data.eligible} subtitle={subtitle} onClose={onClose} />
}

function CancelButton({ onClose, disabled = false }: { onClose: () => void; disabled?: boolean }) {
  return (
    <button type="button" onClick={onClose} disabled={disabled} className="text-xs font-bold text-marine hover:text-marine-dk disabled:opacity-40">
      Cancel
    </button>
  )
}

interface EditWorkerFormProps extends Omit<EditWorkerModalProps, 'workerId'> {
  profile: WorkerProfile
  /** From the worker's Clinical Bridge record. */
  bridgeEligible: boolean
  subtitle: React.ReactNode
}

/** Mounted once the profile and Bridge record are loaded, so the form state starts from them exactly once. */
function EditWorkerForm({ clientId, facility, profile, bridgeEligible, subtitle, onClose }: EditWorkerFormProps) {
  const mutation = useUpdateWorker(clientId, facility.facilityId, profile.workerId)
  // The number's state is on the worker's contacts record (SMS plan 3.5), read beside the profile; an API without it shows the field alone.
  const contactsQuery = useWorkerContacts(profile.workerId)
  const sms = contactsQuery.data?.channels.find((contact) => contact.channel === 'sms') ?? null
  const currentMobile = sms ? { workerId: profile.workerId, contactId: sms.contactId, address: sms.address, awaitingAgreement: sms.awaitingAgreement ?? false, optedOut: sms.optedOut !== null } : null
  const [form, setForm] = useState<WorkerDetailValues>(() => workerDetailValues(profile, bridgeEligible))
  const changes = workerDetailChanges(profile, bridgeEligible, form)
  const hasChanges = Object.keys(changes).length > 0

  function update(next: Partial<WorkerDetailValues>): void {
    setForm((current) => ({ ...current, ...next }))
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault()
    if (!clientId || !hasChanges) {
      return
    }
    await mutation.mutateAsync(changes)
    onClose()
  }

  const canSubmit = Boolean(clientId) && hasChanges && hasWorkerName(form) && !mutation.isPending

  return (
    <Modal
      eyebrow="Edit worker"
      title={profile.name}
      subtitle={subtitle}
      canClose={!mutation.isPending}
      onClose={onClose}
      footer={
        <div className="flex items-center gap-4">
          <Button icon={Save} type="submit" form={FORM_ID} disabled={!canSubmit}>
            {mutation.isPending ? 'Saving…' : 'Save changes'}
          </Button>
          <CancelButton onClose={onClose} disabled={mutation.isPending} />
        </div>
      }
    >
      <form id={FORM_ID} onSubmit={(event) => void handleSubmit(event)} className="bg-canvas p-6 space-y-5">
        <div className="grid gap-5 sm:grid-cols-2">
          <WorkerNameField form={form} onChange={update} inputClass={FORM_INPUT_CLASS} idPrefix={ID_PREFIX} autoFocus />
          <WorkerRoleField form={form} onChange={update} inputClass={FORM_INPUT_CLASS} idPrefix={ID_PREFIX} />
          <WorkerEmploymentField form={form} onChange={update} inputClass={FORM_INPUT_CLASS} idPrefix={ID_PREFIX} />
        </div>
        <WorkerReadingsFields form={form} onChange={update} inputClass={FORM_INPUT_CLASS} idPrefix={ID_PREFIX} />
        <WorkerStaffingFields form={form} onChange={update} inputClass={FORM_INPUT_CLASS} idPrefix={ID_PREFIX} />
        <WorkerBridgeField form={form} onChange={update} idPrefix={ID_PREFIX} />
        <WorkerMobileField form={form} onChange={update} inputClass={FORM_INPUT_CLASS} idPrefix={ID_PREFIX} current={currentMobile} />
        <p className="text-[11px] text-ink-mid">The tier moves when a period closes and the score as outcomes are reported, so neither is edited here.</p>
        <FormError error={mutation.error} fallback="The changes could not be saved." />
      </form>
      {/* Outside the edit form: the login has its own small forms, and saving one never saves the other. */}
      <div className="bg-canvas px-6 pb-6">
        <WorkerLoginSection
          api={operatorWorkerLoginApi(clientId ?? '', facility.facilityId, profile.workerId)}
          queryKey={workerLoginQueryKey(clientId ?? '', profile.workerId)}
          enabled={Boolean(clientId)}
        />
      </div>
    </Modal>
  )
}
